import {useEffect, useState} from "react";
import axios from "axios";
import {API_URL} from "../../../../config";
import {toForecastMonthDate} from "./rsoSalesForecastHelpers";

const emptyStock = (status) => ({status, items:[], asOfUtc:null, dataStatus:null});
const isRecord = (value) => value && typeof value === "object" && !Array.isArray(value);

const readStockResponse = (data, contactInfoId, sourceIds) => {
  if (!isRecord(data) || (data.responsecode !== undefined && String(data.responsecode) !== "200") ||
      (data.responsestatus !== undefined && data.responsestatus !== true && data.responsestatus !== "Success") ||
      (data.error !== undefined && data.error !== null && data.error !== "")) throw new Error("Stock unavailable");
  const payload = data.result === undefined ? data : Array.isArray(data.result)
    ? data.result.length === 1 ? data.result[0] : null : data.result;
  if (!isRecord(payload) || payload.contactInfoId !== contactInfoId || !Array.isArray(payload.items))
    throw new Error("Stock unavailable");
  // Never associate a fact by array position or by the selected replacement.
  const items = payload.items.filter((item) => isRecord(item) && sourceIds.includes(item.productId));
  if (new Set(items.map((item) => item.productId)).size !== items.length) throw new Error("Stock unavailable");
  const dataStatus = ["not_configured","unavailable","partial","available"].includes(payload.status) ? payload.status : null;
  return {status:"ready", items, asOfUtc:payload.asOfUtc || null, dataStatus};
};

export const useRsoMaterialStock = ({enabled, token, month, contactInfoId, sourceProductIds, reloadKey, standards = false}) => {
  const sourceKey = JSON.stringify([...new Set(sourceProductIds.filter((id) => Number.isInteger(id) && id > 0))].sort((a,b) => a-b));
  const forecastMonth = toForecastMonthDate(month);
  const active = !!enabled && !!token && (standards || !!forecastMonth) && Number.isInteger(contactInfoId) && contactInfoId > 0 && sourceKey !== "[]";
  const key = active ? JSON.stringify([token,standards,forecastMonth,contactInfoId,sourceKey,reloadKey]) : "";
  const [result,setResult] = useState({key:"", ...emptyStock("idle")});

  useEffect(() => {
    if (!active) {
      setResult({key:"", ...emptyStock("idle")});
      return undefined;
    }
    const controller = new AbortController();
    let current = true;
    setResult({key, ...emptyStock("loading")});
    axios.get(standards ? `${API_URL}/Dashboard/api/RsoMaterialStandards/stock` : `${API_URL}/Dashboard/api/Dashboard/RsoSalesForecastMaterialStock`, {
      headers:{Authorization:`Bearer ${token}`},
      params:standards ? {contactInfoId} : {forecastMonth,contactInfoId},
      signal:controller.signal,
      timeout:15000,
    }).then((response) => {
      const stock = readStockResponse(response.data,contactInfoId,JSON.parse(sourceKey));
      if (current && !controller.signal.aborted) setResult({key,...stock});
    }).catch(() => {
      // Inventory is informational. It never changes forecast state or exposes
      // backend errors, and a late/cancelled request cannot replace newer facts.
      if (current && !controller.signal.aborted) setResult({key,...emptyStock("unavailable")});
    });
    return () => { current = false; controller.abort(); };
  },[active,key,token,forecastMonth,contactInfoId,sourceKey,standards]);

  // Hide old-account/property/month results during render, before effect cleanup.
  return !active ? emptyStock("idle") : result.key === key
    ? {status:result.status,items:result.items,asOfUtc:result.asOfUtc,dataStatus:result.dataStatus} : emptyStock("loading");
};
